import Head from 'next/head';
import { GetServerSideProps } from 'next';
import { ReactNode, useMemo, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import {
  buildSearchParams, paginatedServerSideQuery, localeHasChanged,
  ApiTalentPageV3, ApiSpokenLanguagesArr, ApiSpokenLanguageLevelsObj,
  PaginatedServerSideQueryResponse,
} from '@utils/data-fetching';
import {
  gsspLanguagesList, gsspCheckEndpointAuth, gsspFetchData, gsspGetLocale,
} from '@utils/gssp';
import { useSwrTalentSearchV3 } from '@hooks/useSwrApi/endpoints/talent-search';
import {
  TalentSearchFilters, useFiltersQueryParams, useFiltersSimilarSearch,
  SaveSearchAlert, ResumeSearchAlerts,
} from '@components/features/filters';
import { TalentList } from '@components/templates/employer/cards-lists/TalentList';
import { LayoutDefault } from '@components/layout/Layouts/LayoutDefault';
import { LanguagesListAndLevelsContext } from '@components/contexts/LanguagesListAndLevels';
import { BannerForJobAdd } from '@components/common/Banner/BannerForJobAdd';
import { useSetHeaderSearchContext } from '@components/contexts/HeaderSearchContext';
import { useApplicantListsMutator } from '@components/templates/employer/cards-lists/hooks/useApplicantListsMutator';

// Interface
// ******************************************
interface TalentSearchProps {
  locale: Locale,
  infiniteQueryParams: PaginatedServerSideQueryResponse,
  talentListFromServer?: ApiTalentPageV3,
  spokenLanguagesList?: ApiSpokenLanguagesArr,
  spokenLanguagesLevels?: ApiSpokenLanguageLevelsObj,
}


// Api keys that need to be an array. The thing is, form multiples (checkbox, select) return a string when only one value
// is selected, and array when multiple values are selected. The API expects arrays for these keys, even if only one value is selected.
// ******************************************
const apiKeysAsArray = ['location', 'domains', 'careerLevels', 'spokenLanguages', 'studyLevels', 'queryFields'];

// Previous locale. Used to check if the locale has changed in getServerSideProps.
// ******************************************
let previousLocale = '';


/**
 * @description Talent search page component
 *
 * - **locale** The locale of the app
 * - **infiniteQueryParams** The query params for the infinite scrolling used for the initial request
 * - **talentListFromServer** The talents data from the server - pass them to the useSwrTalentSearchV3 hook
 * - **spokenLanguagesList** The spoken languages list
 * - **spokenLanguagesLevels** The spoken languages levels
 */
const TalentSearch = (props: TalentSearchProps) => {
  // Destructure props
  const {
    locale,
    infiniteQueryParams,
    talentListFromServer,
    spokenLanguagesList,
    spokenLanguagesLevels,
  } = props;

  // Destructure paginatedQuery
  const {
    page,
    limit,
    objFilterParams,
  } = infiniteQueryParams ?? { page: 1, limit: 24, objFilterParams: {} };

  // Translation
  const { t } = useTranslation('common');


  // Handle query params in browser URL, history and filtering
  // ***********************************************
  const {
    queryParams,
    setQueryParams,
    setCurrentPage,
  } = useFiltersQueryParams({
    serverQueryParams: objFilterParams,
    initialPage: page,
  });


  // isEmptyRef: persists SWR's isEmpty across renders so useFiltersSimilarSearch can read it before SWR.
  const isEmptyRef = useRef<boolean | undefined>(undefined);

  // Similar search: activates when isEmpty=true and filters are set; deactivates when results return.
  // similarSearch is never part of queryParams — appended to the SWR URL only (no oscillation).
  // ***********************************************
  const { similarSearchActive } = useFiltersSimilarSearch({ queryParams, isEmpty: isEmptyRef.current });

  // Fetch talents
  // ***********************************************
  const {
    data,
    size,
    setSize,
    isLoading,
    isValidating,
    isEmpty,
    sizeAsRef,
    mutatePage,
  } = useSwrTalentSearchV3({
    locale,
    limit,
    serverSideData: talentListFromServer,
    startIndex: page,
    urlParams: `${buildSearchParams(queryParams, '&', false, apiKeysAsArray)}${similarSearchActive ? '&similarSearch=1' : ''}`,
  });

  // Persist isEmpty for the next render (used by useFiltersSimilarSearch above).
  isEmptyRef.current = isEmpty;

  const totalPages = data?.at(-1)?.pages || 1;
  const lastLoadedPage = data?.at(-1)?.page || 1;

  // Redux states. With this we watch if:
  // - The CV detail modal is open.
  // - The applicant/talent status has changed (interview, shortlist, etc.).
  // - The applicant/talent has been contacted (message, show contact details).
  // We will mutate the infinite list according to these states.
  // ***********************************************
  useApplicantListsMutator({
    infiniteListData: data,
    swrInfiniteMutatePageFunc: mutatePage,
  });

  // Push search context to the HeaderSearchContext provider (lives above the layout in _app.tsx)
  // ***********************************************
  useSetHeaderSearchContext({ queryParams, setQueryParams });


  // Data for the Languages Context
  // ***********************************************
  const languagesContext = useMemo(() => ({
    spokenLanguagesList,
    spokenLanguagesLevels,
  }), [spokenLanguagesList, spokenLanguagesLevels]);


  // Helpers
  // ***********************************************
  const nrOfTalents = data?.at(-1)?.total || 0;
  const totalText = t('global.label.search.among.talents', { number: nrOfTalents });

  const keyword = queryParams && Object.keys(queryParams)?.length > 0 ? (queryParams as { keyword: string }).keyword : '';


  // Render page component
  // *****************************************************
  return (
    <>
      {/* HEAD - title, meta, etc */}
      <Head>
        <title>{t('cv.list_meta_title')}</title>
        <meta name="description" content={t('candidate.list.meta.description')} />
      </Head>

      {/* PAGE CONTENT */}
      <LanguagesListAndLevelsContext.Provider value={languagesContext}>
        <div className="grow bg-surface-100">

          {/* Filters */}
          <TalentSearchFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />

          <div className="container">
            { /* Similar search info */
              !isEmpty && similarSearchActive && (
                <div className="border-b border-ink/10">
                  <div className="my-4 text-sm font-medium text-ink-medium xl:my-5">
                    {t('cv.search.similarSearch.message')}
                  </div>
                </div>
              )
            }

            {/* Results list */}
            <div className={`flex items-center ${nrOfTalents > 0 ? 'justify-between' : 'justify-end'} my-4 gap-3 xl:my-5`}>

              { /* Total nr of results */
                nrOfTalents > 0 && <div className="text-sm text-ink-medium">{totalText}</div>
              }

              { /* Search alerts */}
              <div className="flex items-start gap-3 lg:divide-x lg:divide-ink/15">
                {/* Save search alert */}
                <SaveSearchAlert
                  locale={locale}
                  savedResumeSearch={data?.at(-1)?.savedResumeSearch}
                  revalidateLastPage={() => {
                    void mutatePage((d) => d.page === size);
                  }}
                />

                {/* Resume search alerts */}
                <ResumeSearchAlerts setQueryParams={setQueryParams} />
              </div>
            </div>

            {/* Show a banner for job add if there is keyword present in search */}
            { keyword && <BannerForJobAdd keyword={keyword} locale={locale} />}

            {/* Infinite list */}
            <TalentList
              trackingData={{
                listType: 'search',
              }}
              data={data}
              size={size}
              setSize={setSize}
              isLoading={isLoading}
              isValidating={isValidating}
              sizeAsRef={sizeAsRef}
              showLoadMore={lastLoadedPage < totalPages}
              isEmpty={isEmpty}
              mutatePage={mutatePage}
              t={t}
              setCurrentPage={setCurrentPage}
              locale={locale}
              spokenLanguagesList={spokenLanguagesList}
              spokenLanguagesLevels={spokenLanguagesLevels}
            />

          </div>

        </div>
      </LanguagesListAndLevelsContext.Provider>
    </>
  );
};


// Page layout
// ************************************************************************************
TalentSearch.getLayout = function getLayout(page: ReactNode) {
  return (
    <LayoutDefault>
      {page}
    </LayoutDefault>
  );
};


// GSSP API
// query and params are needed for the initial request used by the infinite scrolling
// ************************************************************************************
export const getServerSideProps: GetServerSideProps = async (context) => {
  // Destructure context
  const {
    req,
    query,
  } = context;

  const locale = gsspGetLocale(query);

  // Reset the page to 1 if the locale changes. Treat it like a new search, otherwise the infinite scrolling
  // will continue from the last page of the previous locale and this might confuze the user.
  const localeChanged = localeHasChanged(locale, previousLocale);
  previousLocale = locale;
  const { pageNr, ...restQuery } = query;

  // Process the query for the infinite scrolling. Use the returned object to pass parameters
  // to useSwrTalentSearchV3 hook and filters. Don't forget to specify the params that need to be an array.
  const infiniteQueryParams = paginatedServerSideQuery({
    query: localeChanged ? restQuery : query,
    pageSlug: 'pageNr',
    itemsPerPage: 24,
    convertKeysValuesToArray: apiKeysAsArray,
    sortQueryParams: true,
    removeQueryParams: ['locale'],
  });

  // Talents data from server. With this we will pre-populate the infinite scrolling when passing it
  // to the useSwrTalentSearchV3 hook.
  const [talentListFromServer] = await gsspFetchData({ endpoints: [`/v3/talent-search${infiniteQueryParams.fetchUrlParams}`], req, locale });

  // Check if we get an authentication error from the list. If so, redirect to log-in.
  const notLoggedIn = gsspCheckEndpointAuth({ apiResponses: [talentListFromServer], redirectToLogin: true });
  if (typeof notLoggedIn === 'object') return notLoggedIn;

  // Get the spoken languages list and levels from the API (static). Has its own cache.
  const { spokenLanguagesList, spokenLanguagesLevels } = await gsspLanguagesList(req, locale);

  // Return props
  // *********************************
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      locale,
      infiniteQueryParams,
      talentListFromServer,
      spokenLanguagesList,
      spokenLanguagesLevels,
    },
  };
};


// Page export
// ************************************************************************************
export default TalentSearch;
